import React from 'react';
import { Button, Input, message, Spin, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { StylesProps } from 'src/library/types';
import GenericHoc from 'src/common/generic-hoc';
import { HocOptions } from 'src/common/generic-hoc.types';
import { sendLoginOTP } from 'src/api/auth';
import ShipsyLogoUrl from 'src/assets/logos/shipsyLogo.png';

const { Text } = Typography;

const styles = (theme: any) => ({
  logoImage: {
    display: 'flex',
    height: 60,
    margin: '0 auto',
  },
  loginContainer: {
    padding: 24,
    backgroundColor: '#fff',
    boxShadow: '0px 0px 64px rgba(0, 0, 0, 0.12)',
    borderRadius: '8px',
    maxWidth: '100%',
  },
  heading: {
    display: 'flex',
    color: '#333333',
    fontFamily: 'Open Sans',
    fontSize: 24,
    fontWeight: 600,
    letterSpacing: 0,
    margin: '16px auto',
    width: 'fit-content',
  },
  inputBox: {
    display: 'flex',
    marginTop: 3,
    width: 350,
    maxWidth: '100%',
  },
  submitButtonContainer: {
    display: 'flex',
    marginTop: 15,
    width: 350,
    maxWidth: '100%',
  },
  submitButton: {
    backgroundColor: '#006EC3',
  },
  forgotPasswordBtnDiv: {
    textAlign: 'center',
    marginTop: 12,
  },
  subText: {
    textAlign: 'center',
    color: '#666666',

    '& > p': {
      margin: 0,
    },
  },
  centeredContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    flexGrow: 1,
    background: '#EFF5FB',
    minHeight: '100vh',
  },
});

interface Props extends StylesProps<ReturnType<typeof styles>> {
  username: string;
  phone?: string;
  otpUnblockDuration?: number;
  otpUnblockTime?: string;
  organisationDetails?: any;
  onBack?: () => void;
  onSubmitOtp?: (e: any, otp: string) => Promise<void>;
  t: any;
}

const SubmitOtp: React.FC<Props> = (props: Props) => {
  const {
    username,
    phone,
    otpUnblockDuration = 30,
    otpUnblockTime,
    organisationDetails,
    onBack,
    onSubmitOtp,
    classes,
    t,
  } = props;

  const [isSubmitOtpLoading, setIsSubmitOtpLoading] = React.useState(false);
  const [otp, setOtp] = React.useState('');
  const [resendTimer, setResendTimer] = React.useState(0);
  const [canResend, setCanResend] = React.useState(true);
  const [currentOtpUnblockDuration, setCurrentOtpUnblockDuration] = React.useState(
    otpUnblockDuration,
  );

  React.useEffect(() => {
    if (otpUnblockTime) {
      const unblockTime = new Date(otpUnblockTime);
      const now = new Date();
      const remainingSeconds = Math.max(
        0,
        Math.floor((unblockTime.getTime() - now.getTime()) / 1000),
      );
      if (remainingSeconds > 0) {
        setResendTimer(remainingSeconds);
        setCanResend(false);
      }
    }
  }, [otpUnblockTime]);

  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const onClickSubmitOTP = async (e) => {
    e.preventDefault();
    if (!otp) {
      message.error(t('Please enter OTP'));
      return;
    }

    if (!username) {
      message.error(t('Session expired. Please login again.'));
      if (onBack) onBack();
      return;
    }

    setIsSubmitOtpLoading(true);
    if (onSubmitOtp) {
      await onSubmitOtp(e, otp);
    }
    setIsSubmitOtpLoading(false);
  };

  const onClickResendOTP = async () => {
    if (!canResend || !username) return;

    setCanResend(false);
    const response = await sendLoginOTP({ email: username });
    if (response.isSuccess) {
      message.success(t('OTP sent successfully'));
      const newOtpUnblockDuration =
        response.data?.otp_unblock_duration || currentOtpUnblockDuration;
      setCurrentOtpUnblockDuration(newOtpUnblockDuration);
      setResendTimer(newOtpUnblockDuration);
    } else {
      message.error(response.errorMessage);
      setCanResend(true);
    }
  };

  return (
    <div className={classes.centeredContent}>
      <form onSubmit={onClickSubmitOTP} className={classes.loginContainer}>
        <div>
          <img
            className={classes.logoImage}
            src={window.localStorage.getItem('logoUrl') || ShipsyLogoUrl}
            alt="Shipsy"
          />
        </div>
        <div className={classes.subText}>
          <p>{t('Please enter the OTP sent to your registered email')}</p>
        </div>
        <div className={classes.inputBox}>
          <Input
            placeholder={t('Enter OTP')}
            onChange={(e) => setOtp(e.target.value)}
            value={otp}
            maxLength={6}
          />
        </div>
        <div className={classes.submitButtonContainer}>
          <Button
            className={classes.submitButton}
            type="primary"
            onClick={onClickSubmitOTP}
            disabled={!otp || isSubmitOtpLoading}
            block
          >
            {isSubmitOtpLoading ? (
              <Spin
                indicator={
                  <LoadingOutlined style={{ fontSize: 16, color: '#fff' }} spin />
                }
              />
            ) : (
              t('Verify OTP')
            )}
          </Button>
        </div>
        <div className={classes.forgotPasswordBtnDiv}>
          <Text
            onClick={onClickResendOTP}
            style={{
              opacity: canResend ? 1 : 0.5,
              cursor: canResend ? 'pointer' : 'not-allowed',
            }}
          >
            {canResend ? t('Resend OTP') : `${t('Resend OTP in')} ${resendTimer}s`}
          </Text>
        </div>
      </form>
    </div>
  );
};

const hocConfig: HocOptions = {
  connectRedux: { useRedux: false },
  connectJss: { useJss: true, styleSheet: styles },
  connectRouter: false,
  connectTranslession: true,
};
export default GenericHoc(hocConfig)(SubmitOtp);
